import React, { useState, useEffect, useContext } from 'react';
import moment from 'moment-timezone';
import axios from 'axios';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  InputAdornment,
  IconButton,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@mui/material/TextField';
import FetchBillsIcon from '@mui/icons-material/ArrowForwardRounded';
import { makeStyles, withStyles } from '@material-ui/core/styles';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import DeleteIcon from '@material-ui/icons/DeleteOutlineRounded';

import { Loading } from '../stories/Loading';
import PrimaryButton from './lib/buttons/PrimaryButton';
import { FeatureContext } from '../App';

interface LaundryProps {
  accountsView?: boolean;
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: 'auto',
    textAlign: 'start',
    margin: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    textAlign: 'start',
    height: 'calc(100VH - 120px)',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    width: '100VW',
  },
  componentTitle: {
    color: '#142966',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontSize: '24px',
    marginBottom: '0px',
  },
  separatorLine: {
    height: '2px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
    background: '#004878',
    border: 'none',
  },
  mainContainer: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      marginLeft: '40px',
      marginRight: '40px',
    },
    borderRadius: '4px',
    marginBottom: '12px',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '12px',
  },
  formSeparator: {
    background: '#004878',
    width: '2px',
    border: 'none',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: '2px',
    },
    [theme.breakpoints.up('sm')]: {
      width: '2px',
      height: 'auto',
    },
  },
  button: {
    maxWidth: '320px',
    width: '100%',
    marginBottom: '24px',
    marginTop: '24px',
    [theme.breakpoints.up('sm')]: {
      alignSelf: 'center',
    },
  },
  particularsTable: {
    marginTop: '12px',
    marginBottom: '12px',
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
      alignSelf: 'center',
    },
  },
  guestInfoDataKey: {
    flex: 1,
    color: '#004878',
    fontSize: '18px',
    fontWeight: 400,
    padding: '8px',
    verticalAlign: 'top',
  },
  guestInfoDataValue: {
    flex: 1,
    color: '#004878',
    fontSize: '18px',
    fontWeight: 700,
    padding: '8px',
    verticalAlign: 'top',
  },
  deleteIcon: {
    justifyContent: 'center',
    fontSize: 32,
    '&:hover': {
      color: '#c23616',
    },
  },
}));

const RedRadio = withStyles({
  root: {
    '&$checked': {
      color: '#c23616',
    },
  },
  checked: {},
})((props: { [x: string]: any }) => <Radio color="default" {...props} />);

const percentage = (percent: number, total: number) => {
  return (percent / 100) * total;
};

const Laundry = (props: LaundryProps) => {
  const classes = useStyles();
  const features = useContext(FeatureContext);

  const accountsView = props.accountsView || false;
  const [loading, setLoading] = useState(false);
  const [refType, setRefType] = useState<string>('room');
  const [invoiceId, setInvoiceId] = useState<string>();
  const [checkedRooms, setCheckedRooms] = useState(new Array<any>());
  const [laundryInfo, setLaundryInfo] = useState(new Array<any>());

  const [selectedRoom, setSelectedRoom] = useState<any>();
  const [guestInfo, setGuestInfo] = useState<any>();
  const [laundryAmount, setLaundryAmount] = useState<number>();
  const [description, setDescription] = useState<string>();

  useEffect(() => getAllCheckedRooms(), []);
  useEffect(() => fetchLaundryInfo(selectedRoom?.checkin_info_id), [
    selectedRoom,
  ]);

  const reset = () => {
    setSelectedRoom(null);
    setGuestInfo(null);
    setLaundryInfo(new Array<any>());
    setInvoiceId(undefined);
  };

  const getAllCheckedRooms = () => {
    setLoading(true);

    axios
      .get('/api/getAllCheckedRooms')
      .then(({ data }) => {
        setCheckedRooms(data.checkedRooms || []);
      })
      .catch((e) => {
        alert('Error fetching data');
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchLaundryBillsForInvoice = () => {
    setLoading(true);
    axios
      .get(`/api/miscellaneousInfo?invoice_id=${invoiceId}`)
      .then(({ data }) => {
        console.log(data);
        setGuestInfo(data.results.guestInfo);
        setLaundryInfo(data.results.miscInfo);
      })
      .catch((e) => {
        alert('Error fetching data');
        reset();
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchLaundryInfo = (checkinInfoId: number) => {
    if (checkinInfoId > 0) {
      setLoading(true);

      axios
        .get(`/api/miscellaneousInfo?checkin_info_id=${checkinInfoId}`)
        .then(({ data }) => {
          setLaundryInfo(data.results);
        })
        .catch((e) => {
          alert('Failed to fetch miscellaneous info. Try again');
          console.error('Fetch Error: ', JSON.stringify(e));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const deleteTransaction = (info: any) => {
    if (refType === 'room') {
      const checkinInfoId = info.checkin_info_id;
      const transactionId = info.stay_transaction_id;
      setLoading(true);

      axios
        .post('/api/deleteStayTransaction', {
          checkinInfoId,
          transactionId,
        })
        .then(({ data }) => {
          if (data.errno > 0) {
            alert(data.message || 'Some error occured. Try again');
          } else {
            fetchLaundryInfo(checkinInfoId);
          }
        })
        .catch((e) => {
          alert('Some request exception occurred.');
          console.error('Error: ' + JSON.stringify(e));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();

    if (!selectedRoom) {
      alert('Select a room');
    } else if (!(laundryAmount && laundryAmount > 0)) {
      alert('Enter laundry amount greater than 0');
    } else if (!(description && description.trim().length > 0)) {
      alert('Enter description or receipt no');
    } else {
      let checkinInfoId = selectedRoom.checkin_info_id;
      let dateTime = moment().local();
      let transactionDate = dateTime.format('YYYY-MM-DD');
      let transactionTime = dateTime.format('hh:mm:ss');

      if (laundryAmount > 0) {
        setLoading(true);
        axios
          .post('/api/registerMiscellaneous', {
            checkin_info_id: checkinInfoId,
            amount: laundryAmount,
            description,
            transaction_date: transactionDate,
            transaction_time: transactionTime,
          })
          .then((res) => res.data)
          .then((res) => {
            if (res.errno > 0) {
              alert(res.message || 'Some error occured. Try again');
            } else {
              fetchLaundryInfo(checkinInfoId);
              setLaundryAmount(undefined);
              setDescription(undefined);
            }
          })
          .catch((e) => {
            alert('Some request exception occurred.');
            console.error('Error: ' + JSON.stringify(e));
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        alert('Invalid Amount or Description info');
      }
    }
  };

  const getLaundryTable = () => {
    if (laundryInfo && laundryInfo.length > 0) {
      const StyledTableCell = withStyles((theme) => ({
        head: {
          backgroundColor: theme.palette.common.black,
          color: theme.palette.common.white,
        },
        body: {
          fontSize: 16,
        },
      }))(TableCell);

      const StyledTableRow = withStyles((theme) => ({
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.action.hover,
          },
        },
      }))(TableRow);

      return (
        <TableContainer component={Paper} className={classes.particularsTable}>
          <Table aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Date</StyledTableCell>
                <StyledTableCell align="right">Description</StyledTableCell>
                <StyledTableCell align="right">Amount</StyledTableCell>
                <StyledTableCell align="right">GST (18%)</StyledTableCell>
                <StyledTableCell align="right">Total</StyledTableCell>
                <StyledTableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {laundryInfo.map((row) => (
                <StyledTableRow key={row.stay_transaction_id}>
                  <StyledTableCell width={'18%'}>
                    {row.transaction_date
                      ? moment(row.transaction_date).format('MM-DD-YY')
                      : 'N/A'}
                  </StyledTableCell>
                  <StyledTableCell align="right" width={'18%'}>
                    {row.description}
                  </StyledTableCell>
                  <StyledTableCell align="right" width={'18%'}>
                    {row.amount}
                  </StyledTableCell>
                  <StyledTableCell align="right" width={'18%'}>
                    {row.cgst_amount + row.sgst_amount}
                  </StyledTableCell>
                  <StyledTableCell align="right" width={'18%'}>
                    {row.grand_total}
                  </StyledTableCell>
                  <StyledTableCell
                    align="right"
                    style={{
                      justifyItems: 'center',
                    }}
                    width={'10%'}
                  >
                    {accountsView === true || refType !== 'room' ? (
                      []
                    ) : (
                      <DeleteIcon
                        style={{
                          display: 'flex',
                        }}
                        onClick={(_) => deleteTransaction(row)}
                        className={classes.deleteIcon}
                      />
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    } else {
      return '';
    }
  };

  let cgst = percentage(9, laundryAmount || 0);
  let sgst = percentage(9, laundryAmount || 0);
  let grandTotal = ((laundryAmount || 0) + cgst + sgst).toFixed(3);

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <Loading />
      </div>
    );
  } else {
    return (
      <div className={classes.root}>
        <h2 className={classes.componentTitle}>Laundry Service</h2>
        <hr className={classes.separatorLine} />
        <div className={classes.mainContainer}>
          <div className={classes.formContainer}>
            <RadioGroup
              aria-label="Reference Type"
              value={refType || ''}
              onChange={(e) => {
                setRefType(e.target.value);
                reset();
              }}
            >
              <FormControlLabel
                value="room"
                control={<RedRadio />}
                label="Room"
              />
              <FormControlLabel
                value="invoice"
                control={<RedRadio />}
                label="Invoice"
              />
            </RadioGroup>
            <Autocomplete
              options={checkedRooms.sort((a, b) => a.room_name - b.room_name)}
              groupBy={(r: any) =>
                features.groupRoomsByBlockFeatureEnabled
                  ? r.block_name
                  : r.floor_str
              }
              getOptionLabel={(r: any) => r.room_name}
              style={{
                width: 300,
                marginTop: '24px',
                marginBottom: '24px',
                display: refType === 'room' ? 'flex' : 'none',
              }}
              renderInput={(params: { [x: string]: any }) => (
                <TextField {...params} label="Room No" variant="outlined" />
              )}
              onChange={(_, r) => {
                if (r != null) {
                  setSelectedRoom(r);
                } else {
                  setSelectedRoom(undefined);
                  setLaundryInfo([]);
                }
              }}
              value={selectedRoom || null}
            />

            <TextField
              label="Invoice No"
              variant="outlined"
              value={invoiceId}
              onChange={(e) => setInvoiceId(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      edge="end"
                      onClick={fetchLaundryBillsForInvoice}
                    >
                      <FetchBillsIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              style={{
                width: 300,
                marginTop: '32px',
                display: refType !== 'room' ? 'flex' : 'none',
              }}
            />
          </div>
          <hr className={classes.formSeparator} />
          <div
            className={classes.formContainer}
            style={{ display: refType === 'room' ? 'flex' : 'none' }}
          >
            <label
              style={{
                margin: '8px',
                marginBottom: '32px',
                textDecoration: 'underline',
              }}
            >
              Payment Details
            </label>
            <TextField
              label="Laundry Amount"
              variant="outlined"
              type="number"
              placeholder="Ex: 500"
              value={laundryAmount || ''}
              disabled={accountsView}
              onChange={(e) => {
                let amount = parseInt(e.target.value || '0');
                setLaundryAmount(amount);
              }}
              style={{ width: 300 }}
            />
            <TextField
              label="Description"
              variant="outlined"
              placeholder="Laundry - 123"
              value={description || ''}
              disabled={accountsView}
              onChange={(e) => setDescription(e.target.value)}
              style={{ width: 300, marginTop: '32px' }}
            />
            <TextField
              label="GST (18%)"
              disabled
              value={grandTotal || ''}
              style={{ width: 300, marginTop: '24px' }}
            />

            <PrimaryButton
              onClick={handleSubmit}
              className={classes.button}
              disabled={accountsView}
            >
              Save
            </PrimaryButton>
          </div>
          <div
            className={classes.formContainer}
            style={{ display: refType !== 'room' ? 'flex' : 'none' }}
          >
            <label
              style={{
                margin: '8px',
                marginBottom: '32px',
                textDecoration: 'underline',
              }}
            >
              Invoice Details
            </label>
            <table style={{ flex: 1 }}>
              <tbody>
                <tr>
                  <td className={classes.guestInfoDataKey}>Name:</td>
                  <td className={classes.guestInfoDataValue}>
                    {guestInfo?.name}
                  </td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>Check-in Date:</td>
                  <td className={classes.guestInfoDataValue}>
                    {guestInfo?.checkinDate}
                  </td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>Check-out Date:</td>
                  <td className={classes.guestInfoDataValue}>
                    {guestInfo?.checkoutDate}
                  </td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>Room No:</td>
                  <td className={classes.guestInfoDataValue}>
                    {guestInfo?.roomNo}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <hr className={classes.separatorLine} />
        {getLaundryTable()}
      </div>
    );
  }
};

export default Laundry;
